import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { combineLatest, ReplaySubject, Subject } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { takeUntil, withLatestFrom } from 'rxjs/operators';
import { CodeSystem } from '../models/code-system.model';
import { ValueSetReference } from '../models/value-set-reference.model';
import { RosettaService } from '../services/rosetta.service';
import { BasicCodingTableDataSource } from './basic-coding-table.datasource';
import { CodingViewModel } from './coding.viewmodel';
import { ValueSetsDialogComponent } from './value-sets-dialog/value-sets-dialog.component';
import { ValueSetsDialogModel } from './value-sets-dialog/value-sets-dialog.model';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-basic-coding-table',
    templateUrl: './basic-coding-table.component.html',
    styleUrls: ['./basic-coding-table.component.css'],
    providers: [BasicCodingTableDataSource],
    imports: [MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatButton, MatIcon, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatPaginator, AsyncPipe]
})
export class BasicCodingTableComponent implements OnInit, AfterViewInit, OnDestroy {
  dataSource = inject(BasicCodingTableDataSource);
  private rosetta = inject(RosettaService);
  private dialog = inject(MatDialog);

  @Input()
  public search$: Observable<string>;

  @Input()
  public codeSystem$: Observable<CodeSystem>;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  public pageSize = 10;

  public pinnedValueSets: ValueSetReference[] = [];

  private destroy$ = new ReplaySubject<void>(1);

  get relatedTermsColSpan(): number {
    return 3 + this.pinnedValueSets.length;
  }

  ngOnInit(): void {
    combineLatest([this.search$, this.codeSystem$]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([search, codeSystem]) => {
      this.paginator.pageIndex = 0;
      this.dataSource.load(codeSystem.id, 0, this.pageSize, search);
    });

    const cancelSubject = new Subject<void>();

    this.dataSource.codings$.pipe(
      takeUntil(this.destroy$),
      withLatestFrom(this.codeSystem$)
    ).subscribe(([codingVMs, codeSystem]) => {
      cancelSubject.next();

      for (const vm of codingVMs) {
        this.rosetta.getAdditionalInfo(codeSystem.id, vm.code, cancelSubject).subscribe(additionalInfo => {
          vm.relatedTerms = additionalInfo.relatedTerms;
          vm.valueSets = additionalInfo.valueSets;
        });
      }
    });
  }

  ngAfterViewInit(): void {
    this.paginator.page.pipe(
      takeUntil(this.destroy$),
      withLatestFrom(this.search$, this.codeSystem$)
    ).subscribe(([page, search, codeSystem]) => this.dataSource.load(codeSystem.id, page.pageIndex, this.pageSize, search));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  hasRelatedTerms(item: CodingViewModel): boolean {
    return item.relatedTerms && item.relatedTerms.length > 0;
  }

  hasValueSets(item: CodingViewModel): boolean {
    return item.valueSets && item.valueSets.length > 0;
  }

  showValueSets(item: CodingViewModel): void {
    const data = new ValueSetsDialogModel(item.code, item.display, item.valueSets, this.pinnedValueSets);
    const dialogRef = this.dialog.open(ValueSetsDialogComponent, { data });
    dialogRef.afterClosed().pipe(
      takeUntil(this.destroy$)
    ).subscribe(pinnedValueSets => this.pinnedValueSets = pinnedValueSets);
  }

  clearPinnedValueSets(): void {
    this.pinnedValueSets = [];
  }

  getCodingColumns(): string[] {
    return ['code', 'display'].concat(this.pinnedValueSets.map(vs => vs.key)).concat(['valueSets']);
  }
}
